import dayjs from "dayjs";
import { type IntlShape } from "react-intl";
import { Model } from "@binale-tech/shared";
import { getQuarterPeriods, isQuarterPeriod } from "@domain/utils/quarterPeriods";

export interface BwaValueColumn {
    header: string;
    periods: readonly number[];
    /** the Jahr column (landscape PDF); wider cell, excluded from the auto-shrink measure */
    isTotal?: boolean;
}

const { FirstDayOfYear, LastDayOfYear } = Model.Period;
const ALL_PERIODS: readonly number[] = Array.from({ length: 14 }, (_, i) => i);
const MONTHS: readonly number[] = Array.from({ length: 12 }, (_, i) => i + 1);

/** Januar..lastPeriod incl. Saldenvortrag (0); Abschluss (13) joins once December is reached */
export const getBwaYtdPeriods = (lastPeriod: number): number[] => {
    const upTo = Math.min(lastPeriod, 12);
    const periods = Array.from({ length: upTo + 1 }, (_, i) => i);
    return lastPeriod >= 12 ? [...periods, LastDayOfYear] : periods;
};

const monthName = (month: number): string => dayjs.months()[month - 1];
const monthYear = (month: number, year: number): string => `${monthName(month)}\u00a0${year}`;
const range = (intl: IntlShape, from: string, to: string): string =>
    intl.formatMessage({ id: "app.overview.bwa.pdf.range" }, { from, to });

export const getBwaValueColumns = (intl: IntlShape, year: number, period: number | null): BwaValueColumn[] => {
    if (period === null) {
        const months: BwaValueColumn[] = MONTHS.map((month) => ({
            header: monthName(month),
            periods: month === 1 ? [FirstDayOfYear, 1] : month === 12 ? [12, LastDayOfYear] : [month],
        }));
        return [
            ...months,
            { header: intl.formatMessage({ id: "app.fields.date.year" }), periods: ALL_PERIODS, isTotal: true },
        ];
    }
    if (isQuarterPeriod(period)) {
        const quarter = period - 40;
        const lastMonth = quarter * 3;
        return [
            {
                header: intl.formatMessage(
                    { id: "app.overview.bwa.pdf.quarter" },
                    { n: String(quarter), year: String(year) },
                ),
                periods: getQuarterPeriods(period),
            },
            {
                header: range(intl, monthYear(1, year), monthYear(lastMonth, year)),
                periods: getBwaYtdPeriods(lastMonth),
            },
        ];
    }
    if (period === FirstDayOfYear) {
        const header = `01.01.${year}`;
        return [
            { header, periods: [FirstDayOfYear] },
            { header, periods: [FirstDayOfYear] },
        ];
    }
    if (period === LastDayOfYear) {
        return [
            { header: `31.12.${year}`, periods: [LastDayOfYear] },
            { header: range(intl, monthYear(1, year), monthYear(12, year)), periods: getBwaYtdPeriods(LastDayOfYear) },
        ];
    }
    return [
        { header: monthYear(period, year), periods: [period] },
        { header: range(intl, monthYear(1, year), monthYear(period, year)), periods: getBwaYtdPeriods(period) },
    ];
};
